import { cn } from '@/lib/cn';

export interface SegmentOption<T extends string> {
  id: T;
  label: string;
  /** Shown as "Label (n)", so it is part of the accessible name. */
  count?: number;
}

/**
 * A row of segments with one selected. Two meanings, chosen by `semantics`:
 * - `tabs` (default): a `tablist` whose tabs switch the panel named by `panelId`; each tab's id is
 *   `${idPrefix}-${option.id}` so the panel can point back at it.
 * - `buttons`: a `group` of toggle buttons with `aria-pressed`, for a filter or a column switcher that
 *   changes what a list shows rather than which panel is open.
 * `fill` gives every segment the same width.
 */
export function SegmentedTabs<T extends string>({
  label,
  options,
  value,
  onChange,
  semantics = 'tabs',
  idPrefix,
  panelId,
  fill = false,
  className,
}: {
  label: string;
  options: readonly SegmentOption<T>[];
  value: T;
  onChange: (id: T) => void;
  semantics?: 'tabs' | 'buttons';
  idPrefix?: string;
  panelId?: string;
  fill?: boolean;
  className?: string;
}) {
  const tabs = semantics === 'tabs';
  return (
    <div
      role={tabs ? 'tablist' : 'group'}
      aria-label={label}
      className={cn(
        'gap-1 rounded-control bg-surface p-1',
        fill ? 'grid w-full' : 'inline-flex flex-wrap',
        className,
      )}
      style={
        fill ? { gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` } : undefined
      }
    >
      {options.map((option) => {
        const selected = option.id === value;
        return (
          <button
            key={option.id}
            type="button"
            role={tabs ? 'tab' : undefined}
            id={tabs && idPrefix ? `${idPrefix}-${option.id}` : undefined}
            aria-selected={tabs ? selected : undefined}
            aria-controls={tabs ? panelId : undefined}
            aria-pressed={tabs ? undefined : selected}
            onClick={() => onChange(option.id)}
            className={cn(
              'min-h-11 rounded-[6px] px-3 py-1 text-center text-sm leading-tight font-semibold',
              selected ? 'bg-white text-ink shadow-card' : 'text-muted hover:text-ink',
            )}
          >
            {option.label}
            {option.count !== undefined && (
              <span className={cn('tabular-nums', selected ? 'text-muted' : 'text-faint')}>
                {' '}
                ({option.count})
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
